import React from "react";
import { ArrowRight } from "lucide-react";
import { cn, dateLocale } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

const fmt = (cents) => new Intl.NumberFormat(dateLocale(), { style: "currency", currency: "EUR" }).format((cents || 0) / 100);

function Radio({ checked }) {
  return (
    <span
      aria-hidden
      className={cn(
        "h-[18px] w-[18px] rounded-full border-2 shrink-0 flex items-center justify-center transition-colors",
        checked ? "border-sky-400" : "border-muted-foreground/40"
      )}
    >
      {checked && <span className="h-2 w-2 rounded-full bg-sky-400" />}
    </span>
  );
}

/** Compact radio-style plan cards for /billing — 3 paid plans + full-width Enterprise. */
export default function CompactPlanGrid({ plans, current, selected, isTrial, pendingPlanId, unavailablePlans = [], onSelect, onEnterprise }) {
  const paid = plans.filter((p) => !p.contact_sales);
  const enterprise = plans.find((p) => p.contact_sales);

  return (
    <div role="radiogroup" aria-label={tfr("Choisir une offre")} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
      {paid.map((p) => {
        const isCurrent = p.id === current;
        const isUnavailable = unavailablePlans.includes(p.id) && !isCurrent;
        const isSelected = p.id === selected || (isCurrent && !selected);
        const monthly = p.prices?.monthly;
        return (
          <div
            key={p.id}
            role="radio"
            aria-checked={isSelected}
            aria-disabled={isUnavailable || undefined}
            aria-label={`${p.name}, ${monthly ? `${fmt(monthly.amount)} ${tfr("par mois")}` : tfr("sur devis")}. ${tfr(p.tagline)}${isCurrent ? `. ${tfr("Offre actuelle")}` : ""}${isUnavailable ? `. ${tfr("Paiement bientôt disponible")}` : ""}`}
            tabIndex={0}
            onClick={() => !isCurrent && !isUnavailable && onSelect(p.id)}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && !isCurrent && !isUnavailable) { e.preventDefault(); onSelect(p.id); }
            }}
            data-testid={`plan-card-${p.id}`}
            className={cn(
              "relative rounded-[14px] border bg-secondary/30 p-5 transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              isUnavailable ? "cursor-not-allowed opacity-75" : "cursor-pointer",
              p.id === selected
                ? "border-sky-400/80 ring-1 ring-sky-400/60"
                : isCurrent && !selected
                  ? "border-foreground/30"
                  : cn("border-border", !isUnavailable && "hover:border-foreground/25")
            )}
          >
            {p.is_recommended && (
              <span
                className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold px-2.5 py-0.5 rounded-full border border-sky-500/40 bg-surface text-sky-600 dark:text-sky-300 whitespace-nowrap"
                data-testid="plan-badge-recommended"
              >
                {tfr("★ Recommandé")}
              </span>
            )}
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-base font-semibold">{p.name}</h3>
              <Radio checked={isSelected} />
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{tfr(p.tagline)}</p>
            <p className="flex items-baseline gap-1 mt-3">
              <span className="text-2xl font-semibold tabular-nums tracking-tight" data-testid={`plan-price-${p.id}`}>
                {fmt(monthly.amount)}
              </span>
              <span className="text-xs text-muted-foreground">{tfr("/ mois")}</span>
            </p>
            <p className="text-[11px] text-muted-foreground mt-1.5">{tfr(p.short_summary)}</p>
            <div className="mt-2 flex items-center gap-1.5 flex-wrap min-h-[18px]">
              {isCurrent && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-border text-foreground/80" data-testid={`plan-badge-current-${p.id}`}>
                  {isTrial ? tfr("Essai en cours") : tfr("Offre actuelle")}
                </span>
              )}
              {pendingPlanId === p.id && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-500/30 text-amber-300/90" data-testid={`plan-badge-pending-${p.id}`}>
                  {tfr("Programmé")}
                </span>
              )}
              {isUnavailable && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-border text-muted-foreground" data-testid={`plan-badge-unavailable-${p.id}`}>
                  {tfr("Paiement bientôt disponible")}
                </span>
              )}
            </div>
          </div>
        );
      })}

      {enterprise && (
        <button
          type="button"
          onClick={onEnterprise}
          data-testid="plan-card-enterprise"
          className={cn(
            "sm:col-span-2 lg:col-span-3 rounded-[14px] border border-border bg-secondary/30 p-5 text-left",
            "hover:border-foreground/25 transition-colors group",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            "flex items-center justify-between gap-4"
          )}
        >
          <div className="min-w-0">
            <h3 className="text-base font-semibold">{enterprise.name}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{tfr(enterprise.tagline)}</p>
            <p className="text-lg font-semibold mt-2" data-testid="plan-price-enterprise">{tfr("Sur devis")}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{tfr(enterprise.short_summary)}</p>
          </div>
          <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-foreground group-hover:translate-x-0.5 transition-all shrink-0" aria-hidden />
        </button>
      )}
    </div>
  );
}
